/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import * as React from 'react';
import {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from '@web-stories-wp/react';
import styled from 'styled-components';
import {
  Button,
  Icons,
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
  Input,
} from '@web-stories-wp/design-system';
import { __ } from '@web-stories-wp/i18n';
import {
  isPatternEqual,
  type Solid,
  type Pattern,
} from '@web-stories-wp/patterns';
/**
 * Internal dependencies
 */
import CurrentColorPicker from './currentColorPicker';
import GradientPicker from './gradientPicker';
import {
  getGradientRotation,
  updateGradientGeometry,
} from './updateGradientGeometry';
import Header from './header';
import PatternTypePicker from './patternTypePicker';
import useColor from './useColor';
import { INPUT_GAP } from './constants';
import type { SavedColor } from '../../app/savedColors/types';

const Body = styled.div`
  display: flex;
  flex-direction: column;
`;

const InputContainer = styled.div`
  flex: 1;
  margin-bottom: ${INPUT_GAP}px;
`;

const SaveButton = styled(Button)`
  flex-basis: 100%;
`;

function getPatternType(pattern: Solid | Pattern) {
  return 'type' in pattern && pattern.type ? pattern.type : 'solid';
}

type SavedColorEditorProps = {
  geometryBounds?: { width?: number; height?: number };
  legacyPaint?: 'css' | 'svg' | 'filter';
  renderGradientControls?: (
    pattern: Solid | Pattern,
    onCommit: (pattern: Solid | Pattern) => void,
    commitRef: React.MutableRefObject<
      ((pattern: Solid | Pattern) => void) | null
    >
  ) => React.ReactNode;
  allowsGradient: boolean;
  allowsOpacity: boolean;
  children?: React.ReactNode;
  color: SavedColor;
  handleGoBack: () => void;
  handleClose: () => void;
  handleColorChange: (color: SavedColor, debounce?: boolean) => void;
  handleDelete: (color: SavedColor) => void;
  handleSavedColorChange: (color: SavedColor) => void;
  setOnPickerClose: (onClose: () => void) => void;
};

function SavedColorEditor({
  allowsGradient,
  geometryBounds,
  legacyPaint = 'css',
  renderGradientControls,
  allowsOpacity,
  children,
  color,
  handleGoBack,
  handleClose,
  handleColorChange,
  handleDelete,
  handleSavedColorChange,
  setOnPickerClose,
}: SavedColorEditorProps) {
  const isNewColor = color.isLocal;
  const [savedColor, setSavedColor] = useState<SavedColor>(color);
  const originalColor = useRef<SavedColor>(color);
  const lastGeneratedColor = useRef<Solid | Pattern>(color.color);
  const loadedColor = useRef<Solid | Pattern>(color.color);
  const hasEditedColor = useRef(false);
  const canvasCommitRef = useRef<((pattern: Solid | Pattern) => void) | null>(
    null
  );

  const {
    state: { type, stops, currentStopIndex, currentColor, generatedColor },
    actions: {
      load,
      updateCurrentColor,
      reverseStops,
      selectStop,
      addStopAt,
      removeCurrentStop,
      moveCurrentStopBy,
      setToSolid,
      setToGradient,
    },
  } = useColor();
  const currentSavedColor = useMemo(
    () =>
      generatedColor
        ? {
            ...savedColor,
            color: { id: originalColor.current.color.id, ...generatedColor },
          }
        : savedColor,
    [generatedColor, savedColor]
  );
  const canvasPattern = useMemo(
    () => ({
      ...(generatedColor || savedColor.color),
      id: savedColor.color.id,
    }),
    [generatedColor, savedColor.color]
  );

  useEffect(() => {
    if (
      color.color === loadedColor.current &&
      generatedColor &&
      !isPatternEqual(generatedColor, lastGeneratedColor.current)
    ) {
      const newSavedColor = {
        ...originalColor.current,
        color: {
          id: originalColor.current.color.id,
          ...generatedColor,
        },
        id: originalColor.current.color.id,
        error: null,
        // For new colors, keep them local and don't sync until save is clicked
        isLocal: isNewColor,
        shouldSync: false,
      };

      hasEditedColor.current = true;
      handleColorChange(
        newSavedColor,
        getPatternType(generatedColor) ===
          getPatternType(lastGeneratedColor.current)
      );

      lastGeneratedColor.current = generatedColor;
    }
  }, [color.color, handleColorChange, isNewColor, generatedColor]);

  useEffect(() => {
    // History and synced edits update the recipe without starting a new cancel
    // session. The original swatch remains the recipe from editor entry.
    lastGeneratedColor.current = color.color;
    loadedColor.current = color.color;
    setSavedColor((current) =>
      current.color === color.color
        ? current
        : { ...current, color: color.color }
    );
    load(color.color);
  }, [color.color, load]);

  const commitCanvasGradient = (pattern: Solid | Pattern) => {
    hasEditedColor.current = true;
    const nextColor = {
      ...savedColor,
      color: { ...pattern, id: originalColor.current.color.id },
    };
    lastGeneratedColor.current = pattern;
    setSavedColor(nextColor);
    load(pattern);
    handleColorChange(nextColor, false);
  };

  const onSave = useCallback(() => {
    handleSavedColorChange({ ...currentSavedColor, shouldSync: true });
  }, [currentSavedColor]);

  const onCancel = useCallback(() => {
    // Visiting an existing swatch and returning must not dispatch a story-wide
    // colour update. History changes still need the original cancel behaviour.
    if (
      !isNewColor &&
      !hasEditedColor.current &&
      isPatternEqual(color.color, originalColor.current.color)
    ) {
      return;
    }
    setSavedColor(originalColor.current);

    const newSavedColor = {
      ...originalColor.current,
      shouldSync: true,
    };

    if (isNewColor) {
      handleDelete(savedColor);
    } else {
      handleColorChange(newSavedColor, false);
    }
  }, [isNewColor, handleDelete, handleColorChange, savedColor, color.color]);

  const handleGoBackWithCancel = useCallback(() => {
    onCancel();
    // Use setTimeout to ensure the cancel operations complete before navigation
    setTimeout(() => {
      handleGoBack();
    }, 0);
  }, [onCancel, handleGoBack]);

  const handleGoBackSafely = useCallback(() => {
    // For new colors, we need to ensure navigation happens even if delete operations interfere
    if (isNewColor) {
      // Call handleGoBack immediately for new colors to ensure navigation works
      handleGoBack();
      // Then clean up the color state - make sure to remove it from saved colors
      setTimeout(() => {
        handleDelete(savedColor);
      }, 0);
    } else {
      // For existing colors, use the original cancel and navigate flow
      handleGoBackWithCancel();
    }
  }, [
    isNewColor,
    handleGoBack,
    handleDelete,
    savedColor,
    handleGoBackWithCancel,
  ]);

  useEffect(() => {
    setOnPickerClose(() => {
      if (isNewColor) {
        handleDelete(savedColor);
      } else {
        onSave();
      }
    });
    return () => setOnPickerClose(() => {});
  }, [isNewColor, handleDelete, onSave, savedColor, setOnPickerClose]);

  return (
    <>
      <Header
        handleClose={() => {
          if (!isNewColor) {
            onSave();
          }

          handleClose();
        }}
      >
        <Button
          aria-label={__('Go back', 'web-stories')}
          onClick={handleGoBackSafely}
          type={BUTTON_TYPES.TERTIARY}
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.SQUARE}
        >
          <Icons.ArrowLeft />
        </Button>
      </Header>
      {allowsGradient && (
        <PatternTypePicker
          type={type}
          setToGradient={setToGradient}
          setToSolid={setToSolid}
        />
      )}
      <Body>
        {type !== 'solid' && (
          <GradientPicker
            stops={stops}
            currentStopIndex={currentStopIndex}
            onSelect={selectStop}
            onReverse={reverseStops}
            onAdd={addStopAt}
            onDelete={removeCurrentStop}
            onRotate={() =>
              (canvasCommitRef.current || commitCanvasGradient)(
                updateGradientGeometry(canvasPattern, {
                  property: 'rotation',
                  value:
                    getGradientRotation(canvasPattern, geometryBounds) + 0.25,
                  bounds: geometryBounds,
                  legacyPaint,
                })
              )
            }
            onMove={moveCurrentStopBy}
            type={type}
          />
        )}
        {type !== 'solid' &&
          renderGradientControls?.(
            canvasPattern,
            commitCanvasGradient,
            canvasCommitRef
          )}
        <CurrentColorPicker
          color={currentColor}
          onChange={updateCurrentColor}
          showOpacity={allowsOpacity}
        />
        {children}
        <InputContainer>
          <Input
            placeholder={__('Swatch Name', 'web-stories')}
            value={savedColor.name}
            onChange={(evt) =>
              setSavedColor((current) => ({
                ...current,
                name: evt.target.value,
              }))
            }
          />
        </InputContainer>
        <SaveButton
          onClick={() => {
            onSave();
            handleGoBack();
          }}
          type={BUTTON_TYPES.SECONDARY}
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.RECTANGLE}
        >
          {__(isNewColor ? 'Add to Saved Colors' : 'Save', 'web-stories')}
        </SaveButton>
      </Body>
    </>
  );
}

export default SavedColorEditor;
